/**
 * MedPassModal.jsx
 *
 * Record Given / Missed / Refused / PRN for a specific dose.
 */

import React, { useContext, useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
} from "@mui/material";

import { useMutation, gql } from "@apollo/client";
import { UserContext } from "../../../platform/components/auth/auth-context";
import { useSelectedBusiness } from "../../../platform/redux/store";

const SAVE_MAR_ENTRY = gql`
    mutation SaveMarEntry($data: MarEntryInput!) {
        saveMarEntry(data: $data) {
            id
            status
            signature
        }
    }
`;

export default function MedPassModal({ dose, open, onClose, residentId }) {
    const [status, setStatus] = useState("GIVEN");
    const [signature, setSignature] = useState("");
    const [signatureError, setSignatureError] = useState("");
    const [prnReason, setPrnReason] = useState("");
    const [prnEffect, setPrnEffect] = useState("");

    const [saveEntry] = useMutation(SAVE_MAR_ENTRY);
    const selectedBusiness = useSelectedBusiness();
    const { providerUser } = useContext(UserContext);

    const currentStaffInitials = useMemo(() => {
        const staffId = Number(providerUser?.staffId);
        const staff = selectedBusiness?.businessStaffs?.find(
            (s) => Number(s?.id) === staffId
        );

        if (staff?.initials) return String(staff.initials).toUpperCase();

        const username = String(staff?.user?.username || providerUser?.name || "").trim();
        if (!username) return "";
        const parts = username.split(/\s+/);
        const first = parts[0]?.[0] || "";
        const second = (parts.length > 1 ? parts[parts.length - 1]?.[0] : parts[0]?.[1]) || "";
        return (first + second).toUpperCase();
    }, [providerUser?.name, providerUser?.staffId, selectedBusiness?.businessStaffs]);

    const normalizeStatus = (value) => {
        const raw = String(value || "").trim().toUpperCase();
        if (raw === "TAKEN" || raw === "GIVEN") return "GIVEN";
        if (raw === "MISSED") return "MISSED";
        if (raw === "REFUSED") return "REFUSED";
        if (raw === "PRN_GIVEN" || raw === "PRN") return "PRN_GIVEN";
        if (raw === "PRN_MISSED") return "PRN_MISSED";
        return "GIVEN";
    };

    const statusOptions = [
        { value: "GIVEN", label: "Given" },
        { value: "MISSED", label: "Missed" },
        { value: "REFUSED", label: "Refused" },
        { value: "PRN_GIVEN", label: "PRN Given" },
        { value: "PRN_MISSED", label: "PRN Missed" },
    ];

    useEffect(() => {
        if (!open || !dose) return;
        setStatus(normalizeStatus(dose.status));
        setSignature(currentStaffInitials || "");
        setSignatureError("");
        setPrnReason("");
        setPrnEffect("");
    }, [open, dose, currentStaffInitials]);

    const handleSave = async () => {
        if (!dose) return;
        const normalizedStatus = normalizeStatus(status);
        const requiresSignature =
            normalizedStatus === "GIVEN" || normalizedStatus === "PRN_GIVEN";

        if (requiresSignature && !String(currentStaffInitials || "").trim()) {
            setSignatureError("Signature is required for Given/PRN Given.");
            return;
        }

        setSignatureError("");
        await saveEntry({
            variables: {
                data: {
                    residentId,
                    medId: dose.medId,
                    date: dose.date,
                    timeOfDay: dose.timeOfDay,
                    status: normalizedStatus,
                    signature: normalizedStatus === "GIVEN" || normalizedStatus === "PRN_GIVEN"
                        ? String(currentStaffInitials || "").trim().toUpperCase()
                        : String(currentStaffInitials || "").trim(),
                    prnReason: normalizedStatus.startsWith("PRN") ? prnReason : null,
                    prnEffect: normalizedStatus.startsWith("PRN") ? prnEffect : null,
                },
            },
        });

        onClose();
    };

    if (!dose) return null;

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Record Medication</DialogTitle>

            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                    select
                    label="Status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                >
                    {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>

                {status.startsWith("PRN") && (
                    <>
                        <TextField
                            label="Reason"
                            value={prnReason}
                            onChange={(e) => setPrnReason(e.target.value)}
                        />
                        <TextField
                            label="Effect"
                            value={prnEffect}
                            onChange={(e) => setPrnEffect(e.target.value)}
                        />
                    </>
                )}

                <TextField
                    label="Staff Initials"
                    value={signature}
                    disabled
                    error={Boolean(signatureError)}
                    helperText={
                        signatureError ||
                        (!currentStaffInitials
                            ? "No staff initials found. Set staff initials in Staff setup."
                            : "")
                    }
                />
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
